import { useContext, useMemo } from 'react'
import { StateContext } from '../services/StateService'

/**
 * Single source of truth for all PDP product type classifications.
 * Consumed by CTASection, SelectorsContainer, and WhereToBuyController
 * to determine rendering and WTB widget behavior without duplicating derivations.
 * @returns {{ isParentWithVisibleSKUs, isParentWithHiddenSKUs, isParentWithHiddenSKUsAndFilters, isParentWithOneHiddenSKU, isSKU }}
 */
const usePDPProductType = () => {
    const { isParent, moreOptions } = useContext(StateContext)

    return useMemo(
        () => ({
            // Parent with the SKU selector table visible.
            // Table acts as the primary CTA; CTA buttons are demoted by one style.
            isParentWithVisibleSKUs:
                isParent === true && moreOptions?.displayTable === true,

            // Parent whose child SKUs are hidden but selectors exist to refine WTB results.
            // WTB ScreenTip acts as the primary CTA. Backend (PdpCallService._validateSelectorsAvailability)
            // guarantees options are only present when childHasCcSubId, !discontinued, !pdpSelectorOverride.
            isParentWithHiddenSKUs:
                isParent === true &&
                moreOptions?.displayTable === false &&
                moreOptions?.options?.length > 0,

            // isParent is false even though it is a parent (children are hidden by design).
            // Has wtbMmmIds — loads the WTB widget with the full set of child mmmIds.
            isParentWithHiddenSKUsAndFilters:
                (moreOptions?.options?.length === 0 || isParent === false) &&
                moreOptions?.displayTable === false &&
                moreOptions?.wtbMmmIds?.length > 0,

            // Same shape as isParentWithHiddenSKUsAndFilters but without wtbMmmIds.
            // Behaves as a SKU — uses originalMmmid and stockNumber instead.
            isParentWithOneHiddenSKU:
                isParent === false &&
                moreOptions?.displayTable === false &&
                !moreOptions?.wtbMmmIds,

            // Standard SKU or child.
            isSKU: isParent === false && moreOptions?.displayTable === true
        }),
        [isParent, moreOptions]
    )
}

export default usePDPProductType
